import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from "leaflet";
import { Shield, Search, Activity, AlertTriangle, Phone, Lock, 
  AlertCircle, RefreshCw,
  Layers, GitBranch, Info, Circle, Target, Compass, PhoneCall, BarChart2, User,
  
  
  Check, 
  Settings, 
  Wifi, 
  Users, 
  History, 
  Zap, 
  Smartphone, 
  MapPin,
  Radio, Clock } from 'lucide-react';


const PhoneForensicAnalyzer = () => {
  const [inputParts, setInputParts] = useState(['', '', '']);
  const [flagUrl, setFlagUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanPhase, setScanPhase] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [requestTime, setRequestTime] = useState(null);
  const [errors, setErrors] = useState(['', '', '']);
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [threatLevel, setThreatLevel] = useState(null);
  const [threatScore, setThreatScore] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [advancedOptions, setAdvancedOptions] = useState({
    deepScan: false,
    traceRoute: false,
    reverseSearch: false,
    associatedNumbers: false
  });
  
  const [scanStage, setScanStage] = useState('');

  // Reference for map
  const mapRef = useRef(null);

     // Scan stages for animation
      const scanStages = [
        'Initializing scan protocols...',
        'Connecting to carrier databases...',
        'Triangulating geolocation data...',
        'Analyzing network metadata...',
        'Validating carrier information...',
        'Processing regional data...',
        'Finalizing forensic analysis...'
      ];
    
      useEffect(() => {
        let interval;
        if (loading) {
          interval = setInterval(() => {
            setScanProgress(prev => {
              if (prev >= 100) {
                clearInterval(interval);
                return 100;
              }
              // Update scan stage based on progress
              const stageIndex = Math.floor((prev / 100) * scanStages.length);
              setScanStage(scanStages[Math.min(stageIndex, scanStages.length - 1)]);
              return prev + (Math.random() * 3) + 1;
            });
          }, 180);
        } else {
          setScanProgress(0);
          setScanStage('');
        }
        return () => clearInterval(interval);
      }, [loading]);

  // Custom Map Pin Icon 
  const customIcon = useMemo(() => new L.Icon({
    iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  }), []);

  // Format phone number input
  const formatPhoneInput = (index, value) => {
    // Only allow digits
    const sanitizedValue = value.replace(/\D/g, '');
    const newParts = [...inputParts];
    newParts[index] = sanitizedValue;
    setInputParts(newParts);
    setErrors(prev => prev.map((err, i) => (i === index ? '' : err)));
  };

  // Handle keyboard navigation between fields
  const handleKeyDown = (index, event) => {
    if (event.key === 'Enter') {
      const isValid = validateInput(index, inputParts[index]);
      if (isValid) {
        if (index < inputParts.length - 1) {
          document.getElementById(`phone-part-${index + 1}`).focus();
        } else {
          handleSubmit();
        }
      } else {
        setErrors(prev => {
          const newErrors = [...prev];
          newErrors[index] = 'Invalid format';
          return newErrors;
        });
      }
    } else if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      if (index < inputParts.length - 1) {
        document.getElementById(`phone-part-${index + 1}`).focus();
      }
    } else if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      if (index > 0) {
        document.getElementById(`phone-part-${index - 1}`).focus();
      }
    } else if (inputParts[index].length >= (index === 2 ? 7 : 3) && 
               /\d/.test(event.key) && 
               index < inputParts.length - 1) {
      // Auto-advance to next field when max length is reached
      document.getElementById(`phone-part-${index + 1}`).focus();
    }
  };

  // Validate input based on field requirements
  const validateInput = (index, value) => {
    if (index === 0) return /^[0-9]{1,3}$/.test(value);
    if (index === 1) return /^[0-9]{1,3}$/.test(value);
    return /^[0-9]{6,7}$/.test(value);
  };

  // Simulated forensic scan with realistic phases
  const simulateScan = () => {
    const totalDuration = 5500; // Total scan duration in ms
    const phases = [
      { name: 'Initializing connection', duration: 0.15 },
      { name: 'Accessing carrier database', duration: 0.2 },
      { name: 'Verifying number format', duration: 0.1 },
      { name: 'Performing GeoIP lookup', duration: 0.15 },
      { name: 'Analyzing carrier metadata', duration: 0.2 },
      { name: 'Checking network status', duration: 0.1 },
      { name: 'Calculating location precision', duration: 0.05 },
      { name: 'Finalizing results', duration: 0.05 }
    ];
    
    let currentPhase = 0;
    let elapsedTime = 0;
    
    const scanInterval = setInterval(() => {
      if (currentPhase < phases.length) {
        const phase = phases[currentPhase];
        setScanPhase(phase.name);
        
        elapsedTime += phase.duration * totalDuration;
        const progress = Math.min(100, (elapsedTime / totalDuration) * 100);
        setScanProgress(progress);
        
        currentPhase++;
      } else {
        clearInterval(scanInterval);
      }
    }, 300);
    
    return scanInterval;
  };




const handleSubmit = async () => {
  // Validate all fields
  for (let i = 0; i < inputParts.length; i++) {
    if (!validateInput(i, inputParts[i])) {
      setErrors(prev => {
        const newErrors = [...prev];
        newErrors[i] = 'Invalid format';
        return newErrors;
      });
      return;
    }
  }

  const fullPhoneNumber = `+${inputParts[0]}${inputParts[1]}${inputParts[2]}`;
  const startTime = Date.now();
  
  setLoading(true);
  setError(null);
  setScanProgress(0);

  // Start the scan animation
  const scanInterval = simulateScan();

  try {
    // Make API request to get phone data
    const response = await axios.post('/v1/lookup/phone', {
      phone: fullPhoneNumber,
      ...advancedOptions
    });
    
    if (response.data) {
      // Fetch flag using the country code (country code from response)
      const region_code = response.data?.["Phone Details"]?.["ISO Country Code"];

      // Initialize flagUrl variable
      let flagUrl = '';

      if (region_code) {
        try {
          // Fetch flag URL from RestCountries API
          const flagResponse = await axios.get(`https://restcountries.com/v3.1/alpha/${region_code}`);
          flagUrl = flagResponse.data[0]?.flags?.svg; // Get the flag URL
        } catch (error) {
          // Silent error: log it if necessary for debugging
          //console.error("Error fetching flag:", error);
          // flagUrl will remain an empty string if there's an error
        }
      } 

      // Add random threat score for demo purposes
      const randomThreatScore = 10;
      setThreatScore(randomThreatScore);

      if (randomThreatScore < 30) {
        setThreatLevel('Low');
      } else if (randomThreatScore < 70) {
        setThreatLevel('Medium');
      } else {
        setThreatLevel('High');
      }

      // Set the result
      setResult(response.data);

      // Store flag URL in state
      setFlagUrl(flagUrl);

      // Add to search history
      setSearchHistory(prev => [{
        timestamp: new Date().toISOString(),
        phone: fullPhoneNumber,
        location: response.data?.["Location Details"]?.city || 'Unknown',
        carrier: response.data?.["Network Information"]?.carrier || 'Unknown',
        threatLevel: randomThreatScore
      }, ...prev.slice(0, 9)]);
    } else {
      setError("No data found for this number");
    }
  } catch (error) {
    setError(error.response?.data?.error || 'An error occurred during lookup');
  } finally {
    clearInterval(scanInterval);
    setScanProgress(100);
    setLoading(false);
    setRequestTime((Date.now() - startTime) / 1000);

    // Reset scan phase after a brief delay to show 100% completion
    setTimeout(() => {
      setScanPhase('');
    }, 500);
  }
};


const tabKeyMap = {
              details: 'Phone Details',
              location: 'Location Details',
              network: 'Network Information',
              timeline: 'Timeline' 
            };


  // Function to get threat level color
  const getThreatLevelColor = (level) => {
    if (level === 'Low') return 'text-green-400';
    if (level === 'Medium') return 'text-yellow-400';
    return 'text-red-400';
  };

  // When result changes, update the map if needed
  useEffect(() => {
    if (result && result["Location Details"] && mapRef.current) {
      const map = mapRef.current;
      const lat = parseFloat(result["Location Details"].latitude);
      const lng = parseFloat(result["Location Details"].longitude);
      
      map.setView([lat, lng], 13);
      
      // Add an animation effect
      map.flyTo([lat, lng], 13, {
        duration: 1.5,
        easeLinearity: 0.25
      });
    }
  }, [result]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-blue-900/30 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-full bg-blue-500 p-1.5">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                TeleScan Forensics
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center space-x-1 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <History className="w-4 h-4" />
                <span>History</span>
              </button>
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center space-x-1 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Advanced</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Input Panel */}
        <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
            {/* Phone Number Input */}
            <div className="lg:col-span-4 space-y-4">
              <h2 className="text-lg font-medium text-slate-200">Phone Number Analysis</h2>
              <p className="text-sm text-slate-400">Enter the phone number details to perform a comprehensive forensic analysis</p>
              
              <div className="flex flex-wrap md:flex-nowrap items-end gap-4">
                {[
                  {label: 'Country Code', placeholder: '1', maxLength: 3},
                  {label: 'Area Code', placeholder: '555', maxLength: 3},
                  {label: 'Line Number', placeholder: '1234567', maxLength: 7}
                ].map((field, index) => (
                  <div key={index} className="w-full">
                    <label className="block text-sm font-medium text-slate-300 mb-1">{field.label}</label>
                    <div className="relative">
                      <input
                        id={`phone-part-${index}`}
                        type="text"
                        maxLength={field.maxLength}
                        value={inputParts[index]}
                        onChange={(e) => formatPhoneInput(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className={`w-full bg-slate-900/80 border ${errors[index] ? 'border-red-500' : 'border-slate-700'} 
                          focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-4 py-3 
                          text-lg text-blue-300 placeholder-slate-600 transition-all duration-200`}
                        placeholder={field.placeholder}
                      />
                      {errors[index] && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        </div>
                      )}
                    </div>
                    {errors[index] && <p className="mt-1 text-sm text-red-500">{errors[index]}</p>}
                  </div>
                ))}
              </div>
                            
              {/* Advanced Options Panel */}
              {showAdvancedOptions && (
                <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <h3 className="text-sm font-medium text-slate-200 mb-3">Advanced Options</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      {id: 'deepScan', label: 'Deep Scan', icon: Layers},
                      {id: 'traceRoute', label: 'Trace Route', icon: GitBranch},
                      {id: 'reverseSearch', label: 'Reverse Search', icon: Search},
                      {id: 'associatedNumbers', label: 'Find Associates', icon: Users}
                    ].map((option) => (
                      <div key={option.id} className="flex items-center">
                        <input
                          id={option.id}
                          type="checkbox"
                          checked={advancedOptions[option.id]}
                          onChange={(e) => setAdvancedOptions({
                            ...advancedOptions,
                            [option.id]: e.target.checked
                          })}
                          className="h-4 w-4 rounded border-slate-700 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={option.id} className="ml-2 flex items-center text-xs text-slate-300">
                          <option.icon className="w-3 h-3 mr-1" />
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Submit Button and Status */}
            <div className="lg:col-span-3 flex flex-col justify-between space-y-4">
              <div className="flex justify-end items-center space-x-4">
                <div className="text-sm text-slate-400">
                  {requestTime && !loading && (
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>Completed in {requestTime.toFixed(2)}s</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Scan Progress Bar */}
              {loading && (
                <div className="w-full space-y-2">
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>{scanPhase}</span>
                    <span>{scanProgress.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-slate-700/50 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${scanProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 
                  text-white px-6 py-4 rounded-lg font-medium transition-all transform 
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                  shadow-lg shadow-blue-500/20 hover:shadow-blue-600/30"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-3">
                    <div className="relative h-6 w-6">
                      <div className="absolute inset-0 border-2 border-t-transparent border-blue-200 rounded-full animate-spin"></div>
                      <div className="absolute inset-1 border-2 border-t-transparent border-blue-400 rounded-full animate-spin-slow"></div>
                    </div>
                    <span>Analyzing Phone Data</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Zap className="w-5 h-5" />
                    <span>Run Forensic Analysis</span>
                  </div>
                )}
              </button>
              
              {/* Quick Info Cards */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  {label: 'Carrier Check', icon: Radio, color: 'bg-green-500/10 text-green-400'},
                  {label: 'Geo Verification', icon: MapPin, color: 'bg-yellow-500/10 text-yellow-400'},
                  {label: 'Activity Scan', icon: Activity, color: 'bg-purple-500/10 text-purple-400'}
                ].map((item, i) => (
                  <div key={i} className={`rounded-lg p-2 ${item.color} border border-slate-700/50 text-center`}>
                    <item.icon className="w-4 h-4 mx-auto mb-1" />
                    <div className="text-xs font-medium">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Scanning Animation */}
                        {loading && (
                          <div className="bg-slate-900/70 rounded-lg p-6 border border-slate-700/50 my-4">
                            <div className="flex flex-col items-center">
                              <div className="relative w-full max-w-xl h-8 bg-slate-800 rounded-full overflow-hidden mb-2">
                                <div 
                                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-300"
                                  style={{ width: `${scanProgress}%` }}
                                />
                                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                                  <span className="text-white text-xs font-medium">{Math.floor(scanProgress)}% Complete</span>
                                </div>
                              </div>
                              <div className="text-blue-400 text-sm animate-pulse">{scanStage}</div>
                              
                              {/* Scanning Animation */}
                              <div className="mt-6 w-64 h-64 relative">
                                <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                                <div className="absolute inset-8 border-4 border-indigo-500/30 rounded-full"></div>
                                <div className="absolute inset-8 border-4 border-transparent border-t-indigo-500 rounded-full animate-spin" style={{animationDuration: '3s'}}></div>
                                <div className="absolute inset-16 border-4 border-cyan-500/30 rounded-full"></div>
                                <div className="absolute inset-16 border-4 border-transparent border-t-cyan-500 rounded-full animate-spin" style={{animationDuration: '2s'}}></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Phone className="w-16 h-16 text-blue-400 animate-pulse" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

          {/* Error Message */}
          {error && (
            <div className="mt-6 bg-red-900/20 border border-red-800/30 rounded-lg p-4 text-red-400 flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Analysis Failed</p>
                <p className="text-sm opacity-80">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* History Panel */}
        {showHistory && (
          <div className="mt-6 bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-slate-200">Search History</h2>
              <button 
                onClick={() => setSearchHistory([])}
                className="text-xs text-slate-400 hover:text-red-400 transition-colors"
              >
                Clear History
              </button>
            </div>
            
            {searchHistory.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No search history available</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-700/30">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Phone Number</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Location</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Carrier</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Threat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {searchHistory.map((item, index) => (
                      <tr 
                        key={index}
                        className="hover:bg-slate-700/20 transition-colors cursor-pointer"
                        onClick={() => {
                          // Implement history item click to reload that result
                        }}
                      >
                        <td className="px-3 py-2 text-xs text-slate-400">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-2 text-xs text-white">{item.phone}</td>
                        <td className="px-3 py-2 text-xs text-slate-300">{item.location}</td>
                        <td className="px-3 py-2 text-xs text-slate-300">{item.carrier}</td>
                        <td className="px-3 py-2 text-xs">
                          <span 
                            className={`px-2 py-0.5 rounded-full text-xs 
                            ${item.threatLevel < 30 ? 'bg-green-900/20 text-green-400' : 
                              item.threatLevel < 70 ? 'bg-yellow-900/20 text-yellow-400' : 
                              'bg-red-900/20 text-red-400'}`}
                          >
                            {item.threatLevel < 30 ? 'Low' : item.threatLevel < 70 ? 'Medium' : 'High'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Results Panel */}
        {result && !error && (
          <div className="mt-6 bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
            {/* Results Header with Tabs */}
            <div className="bg-slate-900/50 border-b border-slate-700/50 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-white flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-blue-400" />
                  Forensic Analysis Results
                </h2>
                
                {/* Threat Level Indicator */}
                {threatLevel && (
                  <div className="flex items-center space-x-3">
                    <div className="text-sm">
                      <span className="text-slate-400">Threat Level:</span>
                      <span className={`ml-2 font-medium ${getThreatLevelColor(threatLevel)}`}>
                        {threatLevel}
                      </span>
                    </div>
                    <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          threatLevel === 'Low' ? 'bg-green-500' : 
                          threatLevel === 'Medium' ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${threatScore}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Tabs */}
             
            <div className="flex space-x-6 mt-4">
              {[
                { id: 'details', label: 'Details', icon: Info },
                { id: 'location', label: 'Location', icon: MapPin },
                { id: 'network', label: 'Network', icon: Wifi },
                //{ id: 'timeline', label: 'Timeline', icon: Clock }
              ].map((tab) => {
                const key = tabKeyMap[tab.id];
                const tabData = result[key];

                const isEmpty =
                  !tabData || (typeof tabData === 'object' && Object.keys(tabData).length === 0);

                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (isEmpty) {
                        alert(`No ${tab.label.toLowerCase()} data available`);
                        return;
                      }
                      //console.log(`Switching to ${tab.id} tab`);
                      setActiveTab(tab.id);
                    }}
                    className={`flex items-center space-x-1 pb-2 text-sm font-medium border-b-2 transition-colors
                      ${activeTab === tab.id ? 
                        'border-blue-500 text-blue-400' : 
                        'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-700'}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
            </div>
            
            {/* Tab Content */}
            <div className="p-6">
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Phone Details Card */}
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-blue-400" />
                        Phone Details
                      </h3>
                      
                      <div className="space-y-3">
                        {/* Check if result and result['Phone Details'] are not null */}
                        {result && result['Phone Details'] && Object.entries(result['Phone Details']).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                            <span className="text-slate-400">{key}</span>
                            <span className="text-white font-medium">
                              {key === 'country' && value ? (
                                <div className="flex items-center">
                                  {/* Display Flag */}
                                  <img src={flagUrl} alt="Country Flag" className="w-5 h-5 mr-2" />
                                  {value}
                                </div>
                              ) : (
                                value?.toString() || 'N/A'
                              )}
                            </span>
                          </div>
                        ))}
                      </div>

                    
                    {/* Security Status */}
                    <div className="mt-5 pt-3 border-t border-slate-800/50">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-400">Security Status</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                          ${threatLevel === 'Low' ? 'bg-green-900/20 text-green-400' : 
                          threatLevel === 'Medium' ? 'bg-yellow-900/20 text-yellow-400' : 
                          'bg-red-900/20 text-red-400'}`}
                        >
                          {threatLevel}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Network Information Card */}
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                    <h3 className="text-base font-medium text-white mb-4 flex items-center">
                      <Wifi className="w-4 h-4 mr-2 text-blue-400" />
                      Network Information (ISP)
                    </h3>
                    
                    <div className="space-y-3">
                      {Object.entries(result['Network Information'] || {}).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                          <span className="text-slate-400">{key}</span>
                          <span className="text-white font-medium">
                            {key === 'country' && value ? (
                              <div className="flex items-center">
                                {/* Display Flag if country exists */}
                                {flagUrl && <img src={flagUrl} alt="Country Flag" className="w-5 h-5 mr-2" />}
                                {value}
                              </div>
                            ) : (
                              value !== null && value !== undefined ? value.toString() : 'N/A'
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Network Quality */}
                    <div className="mt-5 pt-3 border-t border-slate-800/50">
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-slate-400">Network Quality</span>
                        <span className="text-green-400 font-medium">Excellent</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div className="bg-green-500 h-full rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Location Details Card */}
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                    <h3 className="text-base font-medium text-white mb-4 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-blue-400" />
                      Location Details (ISP)
                    </h3>
                    
                    <div className="space-y-3">
                    {Object.entries(result['Location Details'] || {})
                      .filter(([key]) => !['latitude', 'longitude', 'location'].includes(key))
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2"
                        >
                          <span className="text-slate-400">{key}</span>

                          <span className="text-white font-medium">
                            {key === 'country' && value ? (
                              <div className="flex items-center">
                                <img
                                  src={`https://flagcdn.com/w40/${result['Location Details']['country Code']?.toLowerCase()}.png`}
                                  alt="Flag"
                                  className="w-5 h-5 mr-2"
                                />
                                {value}
                              </div>
                            ) : (
                              value?.toString() || 'N/A'
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Coordinates */}
                    <div className="mt-5 pt-3 border-t border-slate-800/50">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                          <div className="text-xs text-slate-400 mb-1">Latitude</div>
                          <div className="text-sm font-mono text-blue-300">
                            {result["Location Details"]?.latitude || 'N/A'}
                          </div>
                        </div>
                        <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                          <div className="text-xs text-slate-400 mb-1">Longitude</div>
                          <div className="text-sm font-mono text-blue-300">
                            {result["Location Details"]?.longitude || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Location Tab */}
              {activeTab === 'location' && (
                <div className="space-y-6">
                  {/* Map container */}
                  <div className="h-[400px] w-full bg-slate-900/50 rounded-xl overflow-hidden border border-slate-800/80 shadow-inner">
                    {result && result["Location Details"] && (
                        <MapContainer
                            center={[
                              parseFloat(result["Location Details"].latitude),
                              parseFloat(result["Location Details"].longitude)
                            ]}
                            zoom={13}
                            className="h-full w-full"
                            ref={mapRef}
                          >
                            {/* Satellite View WITH Labels */}
                            <TileLayer
                              url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                              subdomains={["mt0", "mt1", "mt2", "mt3"]}
                              attribution="Google Satellite"
                            />

                            {/* Optional: Add terrain layer for 3D effect */}
                            <TileLayer
                              url="https://{s}.google.com/vt/lyrs=t&x={x}&y={y}&z={z}"
                              subdomains={["mt0", "mt1", "mt2", "mt3"]}
                              opacity={0.5}
                            />

                            {/* Marker */}
                            <Marker
                              position={[
                                parseFloat(result["Location Details"].latitude),
                                parseFloat(result["Location Details"].longitude)
                              ]}
                              icon={new L.Icon({
                                iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                                iconSize: [25, 41],
                                iconAnchor: [12, 41],
                                popupAnchor: [1, -34],
                                shadowSize: [41, 41]
                              })}
                            >
                              <Popup className="custom-popup">
                                <div className="text-sm text-gray-800">
                                  <div className="font-bold">
                                    {result["Location Details"].city}, {result["Location Details"].region}
                                  </div>
                                  <div className="text-gray-600">{result["Location Details"].postal}</div>
                                  <div className="mt-1 text-xs">
                                    <span className="inline-block px-1 bg-blue-100 text-blue-800 rounded">
                                      {parseFloat(result["Location Details"].latitude).toFixed(4)}, 
                                      {parseFloat(result["Location Details"].longitude).toFixed(4)}
                                    </span>
                                  </div>
                                </div>
                              </Popup>
                            </Marker>

                            {/* Circle radius */}
                            <Circle
                              center={[
                                parseFloat(result["Location Details"].latitude),
                                parseFloat(result["Location Details"].longitude)
                              ]}
                              radius={2000}
                              pathOptions={{ 
                                color: '#2563EB', 
                                fillColor: '#3B82F6', 
                                fillOpacity: 0.2,
                                weight: 2
                              }}
                            />
                          </MapContainer>
                    )}
                  </div>
                  
                  {/* Location metadata */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(result['Location Details'] || {})
                      .filter(([key]) => !['latitude', 'longitude', 'location'].includes(key))
                      .map(([key, value]) => (
                        <div key={key} className="bg-slate-900/50 rounded-lg p-3 border border-slate-800/80">
                          <div className="text-xs text-slate-400 mb-1">{key}</div>
                          <div className="text-sm font-medium text-white">{value?.toString() || 'N/A'}</div>
                        </div>
                      ))}
                  </div>
                  
                  {/* Advanced location analytics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <Target className="w-4 h-4 mr-2 text-blue-400" />
                        Precision Analysis
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-400">Geo-Precision</span>
                            <span className="text-white">High (±150m)</span>
                          </div>
                          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="bg-green-500 h-full rounded-full" style={{ width: '85%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-400">Data Confidence</span>
                            <span className="text-white">Medium</span>
                          </div>
                          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="bg-yellow-500 h-full rounded-full" style={{ width: '65%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-400">Location Age</span>
                            <span className="text-white">Recent (12hrs)</span>
                          </div>
                          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="bg-blue-500 h-full rounded-full" style={{ width: '90%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <Compass className="w-4 h-4 mr-2 text-blue-400" />
                        Geographic Context
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm border-b border-slate-800/50 pb-2">
                          <span className="text-slate-400">Region Type</span>
                          <span className="text-white">Urban</span>
                        </div>
                        <div className="flex justify-between text-sm border-b border-slate-800/50 pb-2">
                          <span className="text-slate-400">Time Zone</span>
                          <span className="text-white">GMT{result["Location Details"]?.timezone || ''}</span>
                        </div>
                        <div className="flex justify-between text-sm border-b border-slate-800/50 pb-2">
                          <span className="text-slate-400">Local Time</span>
                          <span className="text-white">{new Date().toLocaleTimeString()}</span>
                        </div>
                        <div className="flex justify-between text-sm border-b border-slate-800/50 pb-2">
                          <span className="text-slate-400">Area Population</span>
                          <span className="text-white">1.2M+</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Network Tab */}
              {activeTab === 'network' && (
                <div className="space-y-6">
                  {/* Carrier Information */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <Radio className="w-4 h-4 mr-2 text-blue-400" />
                        Carrier Analysis
                      </h3>
                      
                      <div className="space-y-3">
                        {result['Network Information'] && Object.entries(result['Network Information'])
                          .map(([key, value]) => (
                            <div key={key} className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                              <span className="text-slate-400">{key}</span>
                              <span className="text-white font-medium">{value !== null && value !== undefined ? value.toString() : 'N/A'}</span>
                            </div>
                          ))}
                      </div>
                      
                      {/* Carrier Reputation */}
                      <div className="mt-5 pt-3">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">Carrier Reputation</div>
                            <div className="text-xs text-slate-400">Based on security analysis</div>
                          </div>
                        </div>
                        
                        <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div className="bg-blue-500 h-full rounded-full" style={{ width: '78%' }}></div>
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mt-1">
                          <span>Low Trust</span>
                          <span>High Trust</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Network Traffic Analysis */}
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <Activity className="w-4 h-4 mr-2 text-blue-400" />
                        Network Traffic Analysis
                      </h3>
                      
                      <div className="space-y-4">
                        {['Voice', 'SMS', 'Data'].map((type) => (
                          <div key={type}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-400">{type} Usage</span>
                              <span className="text-white">{Math.floor(Math.random() * 80) + 20}%</span>
                            </div>
                            <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  type === 'Voice' ? 'bg-green-500' :
                                  type === 'SMS' ? 'bg-yellow-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${Math.floor(Math.random() * 80) + 20}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Traffic Patterns */}
                      <div className="mt-6 pt-4 border-t border-slate-800/50">
                        <h4 className="text-sm font-medium text-white mb-3">Traffic Patterns</h4>
                        <div className="space-y-2">
                          {['International', 'Roaming Activity', 'Night Activity', 'Weekend Activity'].map((pattern) => (
                            <div key={pattern} className="flex justify-between items-center text-sm">
                              <span className="text-slate-400">{pattern}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs
                                ${Math.random() > 0.5 ? 'bg-green-900/20 text-green-400' : 'bg-slate-700/50 text-slate-400'}`}
                              >
                                {Math.random() > 0.5 ? 'Detected' : 'None'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Network Security Analysis */}
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                    <h3 className="text-base font-medium text-white mb-4 flex items-center">
                      <Lock className="w-4 h-4 mr-2 text-blue-400" />
                      Network Security Analysis
                    </h3>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        {name: 'Encryption', icon: Lock, value: 'Strong', status: 'good'},
                        {name: 'Vulnerabilities', icon: AlertTriangle, value: 'Low Risk', status: 'good'},
                        {name: 'SIM Swap', icon: Smartphone, value: 'No Activity', status: 'good'},
                        {name: 'Port Activity', icon: Radio, value: 'Suspicious', status: 'warning'},
                      ].map((item) => (
                        <div key={item.name} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                          <div className="flex items-center space-x-2">
                            <div className={`rounded-full p-1.5
                              ${item.status === 'good' ? 'bg-green-900/20 text-green-400' : 
                                item.status === 'warning' ? 'bg-yellow-900/20 text-yellow-400' : 
                                'bg-red-900/20 text-red-400'}`}
                            >
                              <item.icon className="w-3 h-3" />
                            </div>
                            <div className="text-xs text-slate-400">{item.name}</div>
                          </div>
                          <div className="text-sm font-medium text-white mt-2">{item.value}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Security Recommendations */}
                    <div className="mt-6 bg-blue-900/10 border border-blue-900/20 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-white mb-2 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2 text-blue-400" />
                        Security Recommendations
                      </h4>
                      <ul className="space-y-2 text-xs text-slate-300">
                        <li className="flex items-start">
                          <Check className="w-3 h-3 text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                          <span>Enhanced port security recommended due to suspicious activity</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-3 h-3 text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                          <span>Monitor for unusual communication patterns</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-3 h-3 text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                          <span>Verify two-factor authentication implementations</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-6">
                  {/* Activity Timeline */}
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                    <h3 className="text-base font-medium text-white mb-4 flex items-center">
                      <Clock className="w-4 h-4 mr-2 text-blue-400" />
                      Activity Timeline
                    </h3>
                    
                    <div className="relative pl-8 pb-2">
                      {/* Timeline line */}
                      <div className="absolute left-3 top-2 bottom-0 w-0.5 bg-slate-700" />
                      
                      {/* Timeline events */}
                      {[
                        {time: '12:45 PM', date: 'Today', event: 'Phone number analyzed', icon: Search, color: 'bg-blue-500'},
                        {time: '09:32 AM', date: 'Today', event: 'Location data updated', icon: MapPin, color: 'bg-green-500'},
                        {time: '02:18 PM', date: 'Yesterday', event: 'Carrier change detected', icon: RefreshCw, color: 'bg-yellow-500'},
                        {time: '11:05 AM', date: '2 days ago', event: 'Network activity spike', icon: Activity, color: 'bg-purple-500'},
                        {time: '08:45 AM', date: '3 days ago', event: 'First registered activity', icon: PhoneCall, color: 'bg-blue-500'},
                      ].map((event, index) => (
                        <div key={index} className="mb-6 relative">
                          {/* Timeline dot */}
                          <div className={`absolute -left-8 mt-1.5 w-6 h-6 rounded-full ${event.color} flex items-center justify-center`}>
                            <event.icon className="w-3 h-3 text-white" />
                          </div>
                          
                          {/* Time */}
                          <div className="flex items-center text-xs text-slate-400 mb-1">
                            <Clock className="w-3 h-3 mr-1" />
                            <span>{event.time}</span>
                            <span className="mx-2">•</span>
                            <span>{event.date}</span>
                          </div>
                          
                          {/* Event */}
                          <div className="text-sm text-white">{event.event}</div>
                          
                          {/* Event details (for some) */}
                          {index === 0 && (
                            <div className="mt-2 text-xs text-slate-400 bg-slate-800/50 rounded-lg p-2">
                              Full analysis performed on phone number with carrier and location data retrieved.
                            </div>
                          )}
                          {index === 2 && (
                            <div className="mt-2 text-xs text-slate-400 bg-slate-800/50 rounded-lg p-2">
                              Carrier changed from "Example Telecom" to "{result['Network Information']?.carrier || 'Current Carrier'}".
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Pattern Analysis */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <BarChart2 className="w-4 h-4 mr-2 text-blue-400" />
                        Communication Patterns
                      </h3>
                      
                      {/* Fake chart for visualization */}
                      <div className="h-40 w-full mb-3">
                        <div className="flex h-full items-end space-x-1">
                          {Array.from({ length: 24 }).map((_, i) => (
                            <div 
                              key={i} 
                              className="flex-1 bg-blue-500/70 hover:bg-blue-500 transition-all rounded-t-sm"
                              style={{ height: `${10 + Math.random() * 80}%` }}
                              title={`Hour ${i}`}
                            ></div>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                          <span>12 AM</span>
                          <span>12 PM</span>
                          <span>11 PM</span>
                        </div>
                      </div>
                      
                      <div className="text-xs text-slate-400">
                        Activity distribution over 24 hour period based on aggregated carrier data.
                      </div>
                    </div>
                    
                    <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/80">
                      <h3 className="text-base font-medium text-white mb-4 flex items-center">
                        <Users className="w-4 h-4 mr-2 text-blue-400" />
                        Contact Network
                      </h3>
                      
                      {advancedOptions.associatedNumbers ? (
                        <div className="space-y-3">
                          {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center mr-3">
                                  <User className="w-4 h-4 text-slate-300" />
                                </div>
                                <div>
                                  <div className="text-sm text-white">+1 (555) {Math.floor(Math.random() * 900) + 100}-{Math.floor(Math.random() * 9000) + 1000}</div>
                                  <div className="text-xs text-slate-400">Frequent contact</div>
                                </div>
                              </div>
                              <div className="text-xs px-2 py-1 rounded-full bg-blue-900/20 text-blue-400">
                                {Math.floor(Math.random() * 50) + 5} calls
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-40 text-center">
                          <Lock className="w-8 h-8 text-slate-500 mb-2" />
                          <p className="text-sm text-slate-400 mb-1">Contact network analysis unavailable</p>
                          <p className="text-xs text-slate-500">Enable "Find Associates" in advanced options</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      
     
    </div>
  );
};

export default PhoneForensicAnalyzer;
